import { MutableRefObject, useContext, useState, useEffect } from 'react'
import { CardActionAreaProps } from '@mui/material/CardActionArea'
import { useNavigate } from 'react-router-dom'

import LandingPageAccessGuard from '../LandingPageAccessGuard/LandingPageAccessGuard'
import AppCard from '../../atoms/AppCard/AppCard'
import { Route } from '../../../router'
import { isMembershipActive, isAdmin } from '../../../types/Membership'
import { UserContext } from '../../../stores/User/UserStore'
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import useGoogleTagManager from '../../../helpers/analytics/use-google-tag-manager'
import GtmEventName from '../../../helpers/analytics/GtmEventName'
import useUndertakingFormRepository from '../../../repositories/UndertakingForm/use-undertaking-form-repository'
import { UndertakingFormConfig } from '../../../repositories/UndertakingForm/types'

interface UndertakingFormCardProps extends CardActionAreaProps {
    reference?: MutableRefObject<null>
}

const UndertakingFormCard = (props: UndertakingFormCardProps) => {
    const { reference, ...inheritedProps } = props

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { trackCardClick } = useGoogleTagManager()

    const { state: userState } = useContext(UserContext)
    const { retrieveSubmissionStatus, retrieveFormConfig } = useUndertakingFormRepository()

    const [dialogTitle, setDialogTitle] = useState<string>()
    const [dialogMessage, setDialogMessage] = useState<string>()
    const [dialogShow, setDialogShow] = useState(false)
    const [hasSubmitted, setHasSubmitted] = useState(false)
    const [hasDisagreed, setHasDisagreed] = useState(false)
    const [loading, setLoading] = useState(true)
    const [config, setConfig] = useState<UndertakingFormConfig | null>(null)

    useEffect(() => {
        loadData()
    }, [])

    const loadData = async () => {
        setLoading(true)

        // Load config
        const configResult = await retrieveFormConfig()
        if (configResult.data) {
            setConfig(configResult.data)
        }

        // Load submission status (may fail if user not logged in - that's ok)
        try {
            const statusResult = await retrieveSubmissionStatus()
            if (statusResult.data) {
                setHasSubmitted(statusResult.data.hasSubmitted)
                setHasDisagreed(statusResult.data.hasDisagreed)
            }
        } catch (error) {
            // User not logged in - status not available, this is expected behavior
            console.debug(`Unable to retrieve submission status:`, error)
        }

        setLoading(false)
    }

    const showApplicationAlreadySubmittedDialog = () => {
        setDialogTitle(t(`undertaking_form_already_submitted_title`))
        setDialogMessage(t(`undertaking_form_already_submitted_message`))
        setDialogShow(true)
    }

    const showMembershipNotActiveErrorDialog = () => {
        setDialogTitle(t(`programme_application_membership_not_active_title`))
        setDialogMessage(t(`programme_application_membership_not_active_message`))
        setDialogShow(true)
    }

    const handleDialogClose = () => {
        setDialogShow(false)
    }

    const handleCardClick = () => {
        if (loading){
            return
        }
        trackCardClick(GtmEventName.PROGRAMME_APPLICATION_CARD_CLICK, config?.homeCardTitle || config?.title || `Undertaking Form`)

        // If user already submitted and agreed, show dialog
        if (hasSubmitted && !hasDisagreed) {
            showApplicationAlreadySubmittedDialog()
        }
        else if (isMembershipActive(userState.membership?.data) || isAdmin(userState.membership?.data)) {
            navigate(Route.UNDERTAKING_FORM)
        } else {
            showMembershipNotActiveErrorDialog()
        }
    }

    // Show "Submitted" ribbon if user has submitted (regardless of agree/disagree)
    const ribbonText = hasSubmitted ? `SUBMITTED` : `SUBMIT NOW`

    const handleOnLandingPageGuardClickHandled = () => {
        trackCardClick(GtmEventName.PROGRAMME_APPLICATION_CARD_CLICK, `Undertaking Form`)
    }

    // Don't render the card until config is loaded
    if (loading || !config) {
        return null
    }

    const cardTitle = config.homeCardTitle || config.title || `Underwriting Form`
    const cardSummary = config.homeCardSummary || `Indicate your preference for NTUC Membership here`
    const cardImage = config.homeCardImage || ``

    return (
        <Box>
            <LandingPageAccessGuard onClickHandled={handleOnLandingPageGuardClickHandled}>
                <AppCard
                    title={cardTitle}
                    summary={cardSummary}
                    image={cardImage}
                    ribbonText={ribbonText}
                    onClick={handleCardClick}
                    ref={reference}
                    bigger={true}
                    {...inheritedProps}
                />
            </LandingPageAccessGuard>

            <ConfirmationDialog
                confirmationTitle={dialogTitle}
                confirmationText={dialogMessage}
                buttonText={t(`common_button_confirmation`)}
                defaultOpen={dialogShow}
                onConfirm={handleDialogClose}
                acknowledgeOnly />
        </Box>
    )
}

export default UndertakingFormCard
